// Time24Picker.js
import React, { useEffect, useState } from "react";
import { styled } from '@mui/material/styles';
import { Box } from '@mui/system';
import { Button, Popover, IconButton, TextField } from "@mui/material";
import QueryBuilderIcon from '@mui/icons-material/QueryBuilder';
import CheckIcon from '@mui/icons-material/Check';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import { AppStyle, AppWord } from "../value/AppConst";
import IsValid from "../value/IsValid";

const ButtonWidth = 72;
const MaxTimeBoxHeight = 600;
const MaxColumnHeight = 187;

const TimePickerContainer = styled("div")(({ theme }) => ({
    position: "relative", alignItems: "center",
    textAlign: "center", // border: '1px solid #cccccc',  borderRadius: 5,
    borderTopRightRadius: 8, borderBottomRightRadius: 8,
    // padding: '0px 5px 2px 5px',
    marginBottom: 0,
    cursor: "pointer",
    "&:hover": {
        backgroundColor: "#f7f7f7",
    },
}));

const TimeSelectContainer = styled(Box)({
    display: 'table', paddingTop:5
});

const TimeSelectRow = styled(Box)({
    display: 'table-row',
});

const TimeColumn = styled(Box)({
    display: 'table-cell',
    padding: 10,
});

const HourBox = styled(Box)({
    maxHeight: MaxTimeBoxHeight,
    overflow: 'auto',
    borderRadius: 5,
    border: '1px solid #cccccc',
    width: ButtonWidth * 4
});


const HourMinBox = styled(Box)({
    maxHeight: MaxColumnHeight,
    overflow: 'auto',
    borderRadius: 5,
    border: '1px solid #cccccc',
});

const TimeButton = styled(Button)({width: ButtonWidth});

function devideHHmm(value) {
    if(Boolean(value) && value.length === 4)
        return {hhNow: value.substring(0, 2), mmNow: value.substring(2, 4)};
    else
        return {hhNow: null, mmNow: null};
}

const possibleInterval = [1, 2, 3, 5, 6, 10, 12, 15, 20, 30, 60];
function getPossibleInterval(minuteInterval) {
    if(!minuteInterval) return 10;
    let interval = 1;
    for(let i=possibleInterval.length-1; i>=0; i--) {
        if(minuteInterval >= possibleInterval[i]) {
            interval = possibleInterval[i];
            break;
        }
    }
    return interval;
}

export default function Time24Picker({
    dict, //label, id,
    value, // can be 'HHmm' or null or undefined
    onChange,
    disabled,
    disableMinute,
    style,
    size,
    fullWidth,
    label,
    hideLabel,
    minuteInterval, // default 10
    variant,
}) {
    const [time24, setTime24] = useState('');
    const [error, setError] = useState(false);
    const [isFocused, setIsFocused] = useState(false);
    const [minList, setMinList] = useState([]);

    function formatHHmm(value) {
        if(!value || value.length !== 4 || /[^0-9]/.test(value)) return value || '';
        return value.substring(0, 2) + ':' + value.substring(2, 4);
    }
    //const [hhNow, setHhNow] = useState(null);
    //const [mmNow, setMmNow] = useState(null);
    const [anchorEl, setAnchorEl] = useState(null);
    const id = dict.id;
    const controlLabel = label || dict.label;

    useEffect(() => {
        let interval = getPossibleInterval(minuteInterval);
        let minList = [0];
        for(let i=interval; i<60; i+=interval) {
            minList.push(i);
        }
        setMinList(minList);
    }, [minuteInterval]);

    //const {hhNow, mmNow } = devideHHmm(value);
    useEffect(() => {
        if(value) {
            //const {hhNow, mmNow } = devideHHmm(value);
            //setHhNow(hhNow);
            //setMmNow(mmNow);
            setError(IsValid.hhmm4(value) ? false : true);
            setTime24(value);
        }
        else {
            //setHhNow(null);
            //setMmNow(null);
            setError(dict.required ? true : false);
            setTime24('');
        }
    }, [value, dict.required]);

    const onClickPickerButton = (event) => {
        setAnchorEl(event.currentTarget);
    };
  
    const onClosePop = () => {
        setAnchorEl(null);
    };

    const onChangeValue = (value) => {
        if(!Boolean(value) || value.length === 0) {
            //setTime24('');
            const err = dict.required ? true : false;
            setError(err);
            setTime24('');
            onChange(id, null, err);
            return;
        }
        if(value.length > 4) return;
        if(/[^0-9]/.test(value)) return;
        const err = IsValid.hhmm4(value) ? false : true;
        setTime24(value);
        setError(err);
        onChange(id, value, err);
    };

    const onChangeTextInput = (e) => {
        const digitsOnly = (e.target.value || '').replace(/[^0-9]/g, '').substring(0, 4);
        setTime24(digitsOnly);

        if(digitsOnly.length === 4 && IsValid.hhmm4(digitsOnly)) {
            onChangeValue(digitsOnly);
        }
    };

    const onKeyDownTextInput = (e) => {
        if(e.key !== 'Enter') return;

        const digitsOnly = (time24 || '').replace(/[^0-9]/g, '');
        if(digitsOnly.length !== 3 && digitsOnly.length !== 4) return;

        const normalized = digitsOnly.length === 3 ? digitsOnly.padStart(4, '0') : digitsOnly;
        if(!IsValid.hhmm4(normalized)) return;

        e.preventDefault();
        onChangeValue(normalized);
    };

    const onFocusTextInput = () => {
        setIsFocused(true);
        setTime24(value || '');
    };

    const onBlurTextInput = () => {
        setIsFocused(false);
        setTime24(value || '');
    };

    const onClickHour = (hour) => {
        let {mmNow} = devideHHmm(value);
        if(!mmNow) {
            mmNow = "00";
        }
        // onChange(id, hour + mm);
        onChangeValue(hour + mmNow);
    };
  
    const onClickMin = (minute) => {
        let {hhNow} = devideHHmm(value);
        if(!hhNow) {
            hhNow = "00";
        }
        // onChange(id, hhNow + minute);
        onChangeValue(hhNow + minute);
    };

    const onClickClear = () => {
        // onChange(id, null);
        onChangeValue(null);
        onClosePop();
    };

    const conStyle = style ? style : {};
    conStyle.display = fullWidth ? 'flex' : 'inline-flex';
    if(!fullWidth) conStyle.maxWidth = 120;

    const displayLabel = !isFocused && !value && hideLabel ? '--:--' : (hideLabel ? undefined : controlLabel);
    const displayValue = !isFocused && value ? formatHHmm(value) : time24;

    return (
        <>
            <TimePickerContainer style={conStyle}
            >
                <TextField
                    style={{flexGrow:1, margin:0, padding:0, fontSize:'0.9rem'}}
                    value={displayValue}
                    label={displayLabel}
                    disabled={disabled}
                    size={size || AppWord.SMALL}
                    error={error}
                    required={dict.required}
                    placeholder="HHmm"
                    onChange={onChangeTextInput}
                    onKeyDown={onKeyDownTextInput}
                    onFocus={onFocusTextInput}
                    onBlur={onBlurTextInput}
                    sx={{
                        "& .MuiInputBase-input.Mui-disabled": {
                        WebkitTextFillColor: '#339', // Change to your desired color
                        },
                        "& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#999", // Change border color when disabled
                        },
                        "& .MuiInput-underline.Mui-disabled:before": {
                          borderBottomColor: "#999", // Bottom line color when disabled
                        }
                    }}
                    variant={variant}
                />
                <IconButton
                    onClick={onClickPickerButton}
                    size={AppWord.SMALL}
                    disabled={disabled} style={{marginLeft:5,}}
                    color={disabled ? AppStyle.mui.disabled : AppStyle.mui.primary}
                >
                    <QueryBuilderIcon style={{fontSize:'12pt'}} />
                </IconButton>
            </TimePickerContainer>
            <Popover
                open={Boolean(anchorEl)}
                anchorEl={anchorEl}
                onClose={onClosePop}
                anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "right",
                }}
                transformOrigin={{
                    vertical: "top",
                    horizontal: "right",
                }}
            >
                <TimeSelectContainer>
                    <TimeSelectRow>
                        <TimeColumn>
                            <Box style={{fontSize:'0.8rem'}}>시간</Box>
                            <HourBox>
                                {
                                    [...Array(6).keys()].map((hseg) => {
                                        return(
                                            <Box key={hseg}>
                                                {
                                                    [...Array(4).keys()].map((mod) => {
                                                        const hour = hseg * 4 + mod;
                                                        const hh = hour.toString().padStart(2, "0");
                                                        if(Boolean(value) && value.substring(0,2) === hh)
                                                            return(
                                                                <TimeButton
                                                                    key={hh}
                                                                    variant={AppStyle.mui.contained}
                                                                    size={AppWord.SMALL}
                                                                >{hh}</TimeButton>
                                                            );
                                                        else
                                                            return(
                                                                <TimeButton
                                                                    key={hh}
                                                                    disabled={disabled}
                                                                    onClick={() => onClickHour(hh)}
                                                                    size={AppWord.SMALL}
                                                                    variant={AppStyle.mui.text}
                                                                >{hh}</TimeButton>
                                                                )
                                                    })
                                                }
                                            </Box>
                                        )
                                    })
                                }
                            </HourBox>
                        </TimeColumn>
                        <TimeColumn>
                            <Box style={{fontSize:'0.8rem'}}>분</Box>
                            <HourMinBox>
                            {minList.map((minute) => {
                                const mm = minute.toString().padStart(2, "0");
                                return (
                                    <Box key={minute}>
                                        {
                                            Boolean(value) && value.substring(2) === mm
                                            ?
                                            <Button variant="contained" color="primary" size={AppWord.SMALL}>{mm}</Button>
                                            :
                                            <Button onClick={() => onClickMin(mm)} size={AppWord.SMALL}
                                                disabled={disableMinute || disabled}
                                            >
                                                {mm}
                                            </Button>
                                        }
                                    </Box>
                                )
                            })}
                            </HourMinBox>
                            
                        </TimeColumn>
                    </TimeSelectRow>
                </TimeSelectContainer>
                <Box style={{textAlign:'center', marginBottom: 5}}>
                    <Button size={AppWord.SMALL} color={AppStyle.mui.warning} variant={AppStyle.mui.contained} onClick={onClickClear}>
                        <DeleteForeverIcon />지움</Button> &nbsp;
                    <Button size={AppWord.SMALL} onClick={onClosePop} variant={AppStyle.mui.contained}>
                        <CheckIcon /> 닫기</Button>
                </Box>
            </Popover>
        </>
    );

}
